import { FC, useEffect, useState } from 'react'
import {
	Badge,
	Button,
	Card,
	Select,
	Space,
	Table,
	Tag,
	Tooltip,
	Typography,
	message
} from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { useSearchParams } from 'react-router-dom'
import {
	CarrierType,
	Machine,
	fetchCarrierTypes,
	fetchMachines
} from '../api/updates'

const { Title, Text } = Typography

const statusBadge: Record<
	Machine['lastStatus'],
	'success' | 'processing' | 'error' | 'default' | 'warning'
> = {
	success: 'success',
	checking: 'processing',
	downloading: 'processing',
	installing: 'processing',
	failed: 'error'
}

const statusColor: Record<Machine['lastStatus'], string> = {
	success: 'green',
	checking: 'blue',
	downloading: 'blue',
	installing: 'cyan',
	failed: 'red'
}

const UpdatesMachines: FC = () => {
	const [searchParams, setSearchParams] = useSearchParams()
	const [machines, setMachines] = useState<Machine[]>([])
	const [carrierTypes, setCarrierTypes] = useState<CarrierType[]>([])
	const [loading, setLoading] = useState(false)

	const statusFilter = searchParams.get('status') || undefined
	const carrierTypeFilter = searchParams.get('carrierType') || undefined

	const load = async () => {
		setLoading(true)
		try {
			const data = await fetchMachines({
				status: statusFilter,
				carrierType: carrierTypeFilter
			})
			setMachines(data)
		} catch {
			message.error('Failed to load machines')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		fetchCarrierTypes()
			.then(setCarrierTypes)
			.catch(() => {})
	}, [])

	useEffect(() => {
		load()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [statusFilter, carrierTypeFilter])

	const setFilter = (key: string, value: string | undefined) => {
		const next = new URLSearchParams(searchParams)
		if (value) {
			next.set(key, value)
		} else {
			next.delete(key)
		}
		setSearchParams(next)
	}

	const columns = [
		{
			title: 'Machine ID',
			dataIndex: 'machineId',
			key: 'machineId',
			render: (id: string) => <Text code>{id}</Text>
		},
		{
			title: 'Carrier Type',
			dataIndex: 'carrierType',
			key: 'carrierType',
			render: (slug: string) => <Tag>{slug}</Tag>
		},
		{
			title: 'Version',
			dataIndex: 'version',
			key: 'version'
		},
		{
			title: 'Location',
			dataIndex: 'location',
			key: 'location'
		},
		{
			title: 'Status',
			dataIndex: 'lastStatus',
			key: 'lastStatus',
			render: (status: Machine['lastStatus']) => (
				<Badge
					status={statusBadge[status] ?? 'default'}
					text={
						<Tag color={statusColor[status] ?? 'default'}>
							{status}
						</Tag>
					}
				/>
			)
		},
		{
			title: 'Last Seen',
			dataIndex: 'lastSeen',
			key: 'lastSeen',
			render: (v: string | undefined) =>
				v ? (
					<Tooltip title={new Date(v).toISOString()}>
						{new Date(v).toLocaleString('ru')}
					</Tooltip>
				) : (
					'—'
				)
		}
	]

	return (
		<div className="carriers">
			<header className="page-header">
				<Title level={4} style={{ margin: 0 }}>
					Machines
				</Title>
				<Button
					icon={<ReloadOutlined />}
					onClick={load}
					loading={loading}>
					Refresh
				</Button>
			</header>

			<main>
				<Card style={{ marginBottom: 16 }}>
					<Space wrap>
						<Select
							allowClear
							placeholder="Filter by status"
							value={statusFilter}
							onChange={(v) => setFilter('status', v)}
							style={{ width: 180 }}
							options={[
								{ value: 'success', label: 'Success' },
								{ value: 'failed', label: 'Failed' },
								{ value: 'checking', label: 'Checking' },
								{ value: 'downloading', label: 'Downloading' },
								{ value: 'installing', label: 'Installing' }
							]}
						/>
						<Select
							allowClear
							placeholder="Filter by carrier type"
							value={carrierTypeFilter}
							onChange={(v) => setFilter('carrierType', v)}
							style={{ width: 220 }}
							options={carrierTypes.map((ct) => ({
								value: ct.slug,
								label: `${ct.name} (${ct.slug})`
							}))}
						/>
					</Space>
				</Card>

				<Table
					dataSource={machines}
					columns={columns}
					rowKey="machineId"
					loading={loading}
					pagination={{ pageSize: 50 }}
				/>
			</main>
		</div>
	)
}

export default UpdatesMachines
